/**
 * Magazijn — overzichtspagina + ZendingDetail switching.
 *
 * Schakelt tussen overzicht en `ShipmentDetail` via URL-deeplink:
 *   ?section=warehouse                 → overzicht
 *   ?section=warehouse&w=W5999NL...    → zendingdetail
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Warehouse, Plus, Search, Loader2, ArrowRight, Printer, Layers,
  CheckCircle2, Circle, RefreshCw, Package as PackageIcon, Filter,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";

import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";
import WarehousePackageForm, { printLabel } from "./WarehousePackageForm";
import ServiceTypeBadge from "./ServiceTypeBadge";
import DestinationBadge from "./DestinationBadge";
import { ScanButton } from "./ScanButton";
import ShipmentDetail from "./ShipmentDetail";
import ShipmentLookupDialog from "./ShipmentLookupDialog";
import { CustomsInvoiceAction } from "./CustomsInvoiceAction";
import { ShipmentScanResult } from "../operational/ShipmentScanResult";

const SERVICE_FILTERS = [
  { key: "airfreight",    label: "Luchtvracht" },
  { key: "seafreight",    label: "Zeevracht" },
  { key: "ground",        label: "Ground / Wegtransport" },
  { key: "courier",       label: "Courier" },
  { key: "shopdrop_ship", label: "ShopDrop&Ship" },
  { key: "counter_sale",  label: "Balieverkoop" },
];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function WarehouseSection({ initialShipmentId = null, initialPrealertId = null } = {}) {
  // Deeplink: ?w={tracking_number} of ?shipment_id={id} opent ShipmentDetail
  const [activeWNumber, setActiveWNumber] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return initialShipmentId || p.get("shipment_id") || p.get("w");
    } catch { return null; }
  });

  // Keep the exact original ID. W-numbers can be shared by different Agents.
  useEffect(() => {
    if (initialShipmentId) setActiveWNumber(initialShipmentId);
  }, [initialShipmentId]);

  const handleOpenShipment = (wNumber, shipmentId) => {
    setActiveWNumber(shipmentId || wNumber);
    const url = new URL(window.location.href);
    url.searchParams.set("section", "warehouse");
    url.searchParams.delete("w");
    url.searchParams.delete("shipment_id");
    if (shipmentId) url.searchParams.set("shipment_id", shipmentId);
    else if (wNumber) url.searchParams.set("w", wNumber);
    window.history.replaceState({}, "", url);
  };
  const handleBack = () => handleOpenShipment(null);

  if (activeWNumber) {
    return <ShipmentDetail wNumber={activeWNumber} onBack={handleBack} />;
  }
  return <WarehouseOverview onOpenShipment={handleOpenShipment} initialPrealertId={initialPrealertId} />;
}

// ============================================================
// Overview
// ============================================================

function WarehouseOverview({ onOpenShipment, initialPrealertId }) {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("quotes:edit");
  const canLookup = hasPermission("shipments:lookup") ?? true;

  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [hubFilter, setHubFilter] = useState("");
  const [agencyFilter, setAgencyFilter] = useState("");
  const [flowFilter, setFlowFilter] = useState("physical_in_warehouse");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(!!initialPrealertId);
  useEffect(() => { if (initialPrealertId) setCreateOpen(true); }, [initialPrealertId]);
  const [reloadTick, setReloadTick] = useState(0);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [scanNumber, setScanNumber] = useState("");
  const [stats, setStats] = useState({
    total: 0, today: 0,
    in_warehouse: 0, planned_for_consolidation: 0, in_consolidation: 0,
    ready_for_departure: 0, departed: 0, arrived: 0, delivered: 0,
  });
  const [flowMeta, setFlowMeta] = useState([]);
  // Meta: 7 buckets + labels
  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get("/admin/shipping-labels/meta");
        setFlowMeta(r.data?.warehouse_flow_statuses || []);
      } catch { /* niet blokkerend */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { label_type: "warehouse_package", include_expected_agent: true, limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (hubFilter.trim()) params.warehouse_hub = hubFilter.trim();
      if (agencyFilter.trim()) params.agency = agencyFilter.trim();
      if (serviceFilter !== "all") params.service_type = serviceFilter;
      // Iteratie A · flow_bucket param i.p.v. binaire consolidated
      if (flowFilter && flowFilter !== "all") params.flow_bucket = flowFilter;

      const r = await adminApi.get("/admin/shipping-labels", { params });
      const list = r.data.items || [];
      setItems(list);
      setSelectedIds((previous) => previous.filter((id) => list.some((item) => item.id === id)));
      const flowCounts = r.data.flow_counts || {};
      const todayPrefix = todayISO();
      const today = list.filter((it) => (it.created_at || "").startsWith(todayPrefix)).length;
      setStats({
        total: r.data.total ?? list.length, today, ...flowCounts,
      });
    } catch (err) {
      toast.error(formatApiError(err) || "Magazijn laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [q, hubFilter, agencyFilter, flowFilter, serviceFilter]);

  useEffect(() => { load(); }, [load, reloadTick]);

  const triggerReload = () => setReloadTick((n) => n + 1);

  // Scan in zoekbalk → direct openen indien exact W-nr match
  const handleScannedSearch = async (value) => {
    setQ(value);
    if (canCreate && canLookup) { setScanNumber(value.trim()); return; }
    try {
      const r = await adminApi.get("/admin/shipping-labels", { params: { q: value, label_type: "warehouse_package", include_expected_agent: true, limit: 5 } });
      const exact = (r.data.items || []).filter(
        (it) => (it.tracking_number || "").toUpperCase() === value.toUpperCase()
                || (it.linked_external_tracking_number || "").toUpperCase() === value.toUpperCase(),
      );
      const complete = (r.data.total ?? 0) <= (r.data.items || []).length;
      if (exact.length === 1 && complete) onOpenShipment(exact[0].tracking_number, exact[0].id);
      else if (exact.length > 1 || !complete) setLookupOpen(true);
    } catch { /* negeren */ }
  };

  return (
    <div className="space-y-4" data-testid="warehouse-section">
      <div className="bg-gradient-to-r from-[#082c50] to-[#0a3a6b] text-white px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Warehouse className="w-6 h-6 text-yellow-400" />
          <div>
            <h1 className="font-chivo font-black text-2xl tracking-tight">Magazijn</h1>
            <p className="text-xs text-blue-100">Registreer fysieke zendingen — direct beschikbaar voor consolidatie</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-1 sm:flex-initial sm:min-w-[320px]">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Tracking of W-nummer"
                   value={q} onChange={(e) => { setQ(e.target.value); if (!e.target.value) setScanNumber(""); }}
                   onKeyDown={e => { if (e.key === "Enter") handleScannedSearch(q); }}
                   onBlur={() => { if (q.trim()) setScanNumber(q.trim()); }}
                   className="pl-8 rounded-full bg-white text-gray-900 h-9 text-sm"
                   data-testid="warehouse-search" />
          </div>
          <ScanButton iconOnly testId="warehouse-search-scan"
                      title="Tracking of W-nummer scannen"
                      onResult={handleScannedSearch} className="bg-white" />
        </div>
      </div>

      <ShipmentScanResult number={scanNumber} onShipment={onOpenShipment} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="warehouse-stats">
        <StatCard icon={PackageIcon} label="Totaal (huidige filter)" value={stats.total} accent="blue" testId="warehouse-stat-total" />
        <StatCard icon={CheckCircle2} label="Vandaag binnengekomen" value={stats.today} accent="emerald" testId="warehouse-stat-today" />
        <StatCard icon={Circle} label="In Warehouse (los)" value={stats.in_warehouse || 0} accent="amber" testId="warehouse-stat-in-warehouse" />
        <StatCard icon={Layers} label="Gepland/In consolidatie" value={(stats.planned_for_consolidation || 0) + (stats.in_consolidation || 0) + (stats.ready_for_departure || 0)} accent="indigo" testId="warehouse-stat-in-cons" />
      </div>

      {/* Iteratie A · flow-tabs. VERVANGT binaire consolidated-filter zodat
          shipments NOOIT verdwijnen door alleen consolidation-koppeling. */}
      <div className="bg-white border border-gray-200 p-1 overflow-x-auto"
           data-testid="warehouse-flow-tabs">
        <div className="flex flex-wrap gap-1">
          <FlowTab active={flowFilter === "physical_in_warehouse"}
                   onClick={() => setFlowFilter("physical_in_warehouse")}
                   testId="warehouse-flow-tab-physical"
                   label="Fysiek in Warehouse"
                   count={(stats.in_warehouse || 0) + (stats.planned_for_consolidation || 0) + (stats.in_consolidation || 0) + (stats.ready_for_departure || 0)}
                   tone="brand" />
          {flowMeta.map((m) => (
            <FlowTab key={m.key} active={flowFilter === m.key}
                     onClick={() => setFlowFilter(m.key)}
                     testId={`warehouse-flow-tab-${m.key}`}
                     label={m.label}
                     count={stats[m.key] || 0}
                     tone={m.physical_in_warehouse ? "emerald" : "slate"} />
          ))}
          <FlowTab active={flowFilter === "all"}
                   onClick={() => setFlowFilter("all")}
                   testId="warehouse-flow-tab-all"
                   label="Alle statussen"
                   count={null}
                   tone="slate" />
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <CustomsInvoiceAction shipmentIds={selectedIds} />
        {selectedIds.length > 0 && <span className="text-xs text-slate-600" data-testid="warehouse-selected-count">{selectedIds.length} geselecteerd</span>}
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)}
                  className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none font-chivo font-bold uppercase text-xs h-10 px-4"
                  data-testid="warehouse-new-package-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nieuwe zending registreren
          </Button>
        )}
        {canLookup && (
          <Button onClick={() => setLookupOpen(true)}
                  variant="outline"
                  className="rounded-none border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white font-chivo font-bold uppercase text-xs h-10 px-4"
                  data-testid="warehouse-lookup-btn">
            <Search className="w-4 h-4 mr-1.5" /> Zending opzoeken
          </Button>
        )}
        <Button variant="outline" onClick={triggerReload}
                className="rounded-none text-xs h-10" data-testid="warehouse-reload">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Vernieuwen
        </Button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          <Input placeholder="Hub…" value={hubFilter}
                 onChange={(e) => setHubFilter(e.target.value)}
                 className="w-32 h-9 text-xs rounded-none" data-testid="warehouse-filter-hub" />
          <Input placeholder="Agentschap…" value={agencyFilter}
                 onChange={(e) => setAgencyFilter(e.target.value)}
                 className="w-36 h-9 text-xs rounded-none" data-testid="warehouse-filter-agency" />
          <Select value={serviceFilter} onValueChange={setServiceFilter}>
            <SelectTrigger className="w-40 h-9 text-xs rounded-none" data-testid="warehouse-filter-service">
              <SelectValue placeholder="Diensttype" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle diensttypes</SelectItem>
              {SERVICE_FILTERS.map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`warehouse-filter-service-${s.key}`}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="warehouse-list">
        {loading ? (
          <div className="p-10 text-center text-gray-500">
            <Loader2 className="w-5 h-5 inline animate-spin mr-2" />Laden…
          </div>
        ) : items.length === 0 ? (
          <EmptyState canCreate={canCreate} onCreate={() => setCreateOpen(true)} />
        ) : (
          <table className="block w-full text-sm lg:table lg:table-fixed">
            <thead className="hidden lg:table-header-group bg-gray-50 text-[10px] uppercase font-bold text-gray-600 tracking-wide">
              <tr>
                <th className="w-10 p-2"><input type="checkbox" aria-label="Alle zichtbare zendingen selecteren" data-testid="warehouse-select-all" checked={items.length > 0 && selectedIds.length === items.length} onChange={(e) => setSelectedIds(e.target.checked ? items.map((item) => item.id) : [])} /></th>
                <th className="text-left p-3">W-nummer</th>
                <th className="text-left p-3">Aangemaakt</th>
                <th className="text-left p-3">Diensttype</th>
                <th className="text-left p-3">Bestemming</th>
                <th className="text-left p-3">Afzender → Ontvanger</th>
                <th className="text-right p-3">Colli / kg</th>
                <th className="text-right p-3">CBM</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Actie</th>
              </tr>
            </thead>
            <tbody className="block lg:table-row-group">
              {items.map((it) => {
                const cbm = computeCbm(it.shipment_details);
                const cons = it.consolidation_summary;
                const flow = it.warehouse_flow_status;
                const flowLabel = it.warehouse_flow_label;
                return (
                  <tr key={it.id} className="grid grid-cols-2 lg:table-row border-t border-gray-100 hover:bg-gray-50 cursor-pointer [&>td]:min-w-0 [&>td]:break-words [&>td]:[overflow-wrap:anywhere]"
                      onClick={() => onOpenShipment(it.tracking_number, it.id)}
                      data-testid={`warehouse-row-${it.id}`}>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={`Selecteer ${it.tracking_number}`} data-testid={`warehouse-select-${it.id}`} checked={selectedIds.includes(it.id)} onChange={(e) => setSelectedIds((ids) => e.target.checked ? [...ids, it.id] : ids.filter((id) => id !== it.id))} /></td>
                    <td className="p-3 font-mono text-xs font-bold text-[#082c50]">
                      {it.tracking_number}
                      {it.linked_external_tracking_number && (
                        <div className="text-[10px] text-gray-400 font-normal">ext: {it.linked_external_tracking_number}</div>
                      )}
                      {cons?.consolidation_number && (
                        <div className="text-[10px] text-indigo-700 font-bold mt-0.5"
                             data-testid={`warehouse-row-cons-number-${it.id}`}>
                          <Layers className="inline w-2.5 h-2.5 mr-0.5" />{cons.consolidation_number}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-xs text-gray-600">
                      {fmt(it.created_at)}
                      <div className="text-[10px] text-gray-400">{it.created_by_email}</div>
                    </td>
                    <td className="p-3">
                      <ServiceTypeBadge serviceType={it.service_type} testId={`warehouse-row-service-${it.id}`} />
                    </td>
                    <td className="p-3">
                      <DestinationBadge code={it.destination_country_code}
                                        name={it.destination_country_name}
                                        testId={`warehouse-row-destination-${it.id}`} />
                      {cons?.etd && (
                        <div className="text-[10px] text-slate-500 mt-1" data-testid={`warehouse-row-etd-${it.id}`}>
                          ETD {new Date(cons.etd).toLocaleDateString("nl-NL")}
                        </div>
                      )}
                      {cons?.eta && (
                        <div className="text-[10px] text-slate-500" data-testid={`warehouse-row-eta-${it.id}`}>
                          ETA {new Date(cons.eta).toLocaleDateString("nl-NL")}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      <div className="font-semibold text-[#082c50]">{it.sender?.name || "—"}</div>
                      <div className="text-[10px] text-gray-400 truncate"><ArrowRight className="inline w-2.5 h-2.5" /> {it.recipient?.name || "—"} · {it.recipient?.city || ""}</div>
                    </td>
                    <td className="p-3 text-right text-xs font-mono">
                      {it.shipment_details?.piece_count ?? 0} colli<br />
                      <span className="text-gray-500">{(it.shipment_details?.weight_kg ?? 0).toFixed(1)} kg</span>
                    </td>
                    <td className="p-3 text-right text-xs font-mono text-gray-600">{cbm.toFixed(4)} m³</td>
                    <td className="p-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {flow && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase font-bold tracking-wide border rounded ${
                            flow === "in_warehouse" ? "bg-amber-50 text-amber-800 border-amber-200"
                            : flow === "planned_for_consolidation" ? "bg-indigo-50 text-indigo-800 border-indigo-200"
                            : flow === "in_consolidation" ? "bg-indigo-100 text-indigo-900 border-indigo-300"
                            : flow === "ready_for_departure" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                            : flow === "departed" ? "bg-blue-100 text-blue-900 border-blue-300"
                            : flow === "arrived" ? "bg-cyan-50 text-cyan-800 border-cyan-200"
                            : "bg-slate-100 text-slate-800 border-slate-300"
                          }`}
                                data-testid={`warehouse-row-flow-${it.id}`}>
                            {flowLabel || flow}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={(e) => { e.stopPropagation(); printLabel(it); }}
                              data-testid={`warehouse-row-print-${it.id}`} title="Label printen">
                        <Printer className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <WarehousePackageForm
        initialPrealertId={initialPrealertId}
        open={createOpen}
        onClose={() => { setCreateOpen(false); triggerReload(); }}
        onCreated={(created, intent) => {
          triggerReload();
          if (intent === "open" && created?.tracking_number) {
            onOpenShipment(created.tracking_number, created.id);
          }
        }}
      />
      {lookupOpen && (
        <ShipmentLookupDialog
          initialQuery={q}
          onClose={() => setLookupOpen(false)}
          onOpenShipment={(ship) => {
            if (ship?.id) onOpenShipment(ship.tracking_number, ship.id);
          }}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = "blue", testId }) {
  const map = {
    blue:    "border-blue-200 bg-blue-50 text-blue-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber:   "border-amber-200 bg-amber-50 text-amber-900",
    indigo:  "border-indigo-200 bg-indigo-50 text-indigo-900",
  };
  return (
    <div className={`border ${map[accent]} px-4 py-3 rounded-md flex items-center gap-3`} data-testid={testId}>
      <Icon className="w-6 h-6 opacity-80" />
      <div>
        <div className="text-[10px] uppercase font-bold tracking-wide opacity-70">{label}</div>
        <div className="font-chivo font-black text-2xl leading-none">{value}</div>
      </div>
    </div>
  );
}

function FlowTab({ active, onClick, label, count, tone = "slate", testId }) {
  const toneCls = active
    ? (tone === "brand" ? "bg-[#082c50] text-white border-[#082c50]"
       : tone === "emerald" ? "bg-emerald-600 text-white border-emerald-600"
       : "bg-slate-800 text-white border-slate-800")
    : (tone === "brand" ? "bg-white text-[#082c50] border-slate-200 hover:bg-slate-50"
       : tone === "emerald" ? "bg-white text-emerald-800 border-slate-200 hover:bg-emerald-50"
       : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50");
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className={`flex-1 min-w-[110px] px-3 py-2 border text-[11px] font-bold uppercase tracking-wide text-center transition-colors ${toneCls}`}>
      <div>{label}</div>
      {count !== null && count !== undefined && (
        <div className={`text-lg font-black font-mono ${active ? "text-white" : "text-slate-900"}`}>
          {count}
        </div>
      )}
    </button>
  );
}

function EmptyState({ canCreate, onCreate }) {
  return (
    <div className="p-12 text-center" data-testid="warehouse-empty">
      <Warehouse className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <h3 className="font-chivo font-black text-lg text-[#082c50]">Nog geen zendingen in het magazijn</h3>
      <p className="text-sm text-gray-500 mt-1 mb-4">
        Registreer fysieke zendingen zodra ze binnenkomen. Elke zending krijgt automatisch een W-nummer.
      </p>
      {canCreate && (
        <Button onClick={onCreate} className="bg-[#0055FF] text-white rounded-none font-bold uppercase text-xs"
                data-testid="warehouse-empty-create">
          <Plus className="w-4 h-4 mr-1.5" /> Eerste zending registreren
        </Button>
      )}
    </div>
  );
}

function computeCbm(sd) {
  if (!sd) return 0;
  const items = sd.items || [];
  if (items.length > 0) {
    return items.reduce((s, it) => s + (Number(it.cbm) || 0), 0);
  }
  const L = sd.length_cm || 0, W = sd.width_cm || 0, H = sd.height_cm || 0;
  const count = sd.piece_count || 1;
  return (L * W * H * count) / 1_000_000;
}
